/**
 * Изменяемая ширина правой панели-инспектора (UI-2, п.12) — общий код для аукционов,
 * проектов и задач (.task-inspector). Слева полоса-захват: перетаскивание задаёт ширину от
 * 360px до 80% окна, двойной клик — ширина по умолчанию. Кнопка «Развернуть» в шапке
 * раскрывает панель на всю ширину контента. Ширина — в localStorage отдельно для каждого
 * типа (ccpm_inspector_width_<key>). На окне уже 900px панель всегда во весь экран (CSS).
 *
 * maximized (УИ-2d) — стартовать развёрнутой (ti-maximized). Нужно богатой карточке ИЛ
 * (ключ `sheets`): шапка-форма и левое меню «как в 1С» читаемы только во всю ширину.
 * Пользователь сворачивает кнопкой «Свернуть». По умолчанию false — другие панели как были.
 *
 * Панель шлёт bubbling-событие 'ccpm:inspector-resize' { width } (0 — закрыта): экран
 * аукционов по нему сжимает таблицу. Gantt панель оставляет поверх (views/gantt.js не трогаем).
 *
 *   const r = attachInspectorResize(root, { key: 'tender', defaultWidth: 520 });
 *   r.notify();   // после открытия/закрытия панели
 */
const MIN_WIDTH = 360;
const MAX_SHARE = 0.8;
const MOBILE_MAX_WIDTH = 900;

const clamp = (w) => Math.round(Math.max(MIN_WIDTH, Math.min(w, window.innerWidth * MAX_SHARE)));

export function attachInspectorResize(root, { key, defaultWidth, maximized: startMaximized = false }) {
  let storeKey = `ccpm_inspector_width_${key}`;
  let width = defaultWidth;
  try {
    const saved = Number(localStorage.getItem(storeKey));
    if (saved > 0) width = saved;
  } catch { /* storage недоступен */ }
  let maximized = startMaximized;

  root.classList.add('ti-resizable');
  const grip = document.createElement('div');
  grip.className = 'ti-grip';
  grip.dataset.tiGrip = key;
  grip.title = 'Ширина панели (двойной клик — по умолчанию)';
  root.prepend(grip);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'ti-expand';
  btn.dataset.tiExpand = key;
  root.querySelector('.ti-header')?.insertBefore(btn, root.querySelector('.ti-close'));

  const save = () => {
    try {
      if (width === defaultWidth) localStorage.removeItem(storeKey);
      else localStorage.setItem(storeKey, String(width));
    } catch { /* storage недоступен */ }
  };

  const notify = () => {
    const open = !root.hidden && root.classList.contains('ti-open');
    const w = open ? root.getBoundingClientRect().width : 0;
    root.dispatchEvent(new CustomEvent('ccpm:inspector-resize', { bubbles: true, detail: { key, width: w } }));
  };

  const apply = () => {
    root.style.setProperty('--ti-width', maximized ? '100%' : `${clamp(width)}px`);
    root.classList.toggle('ti-maximized', maximized);
    btn.textContent = maximized ? 'Свернуть' : 'Развернуть';
    btn.title = maximized ? 'Вернуть прежнюю ширину' : 'На всю ширину';
    btn.setAttribute('aria-pressed', String(maximized));
    notify();
  };

  grip.addEventListener('pointerdown', (e) => {
    if (window.innerWidth < MOBILE_MAX_WIDTH) return;
    e.preventDefault();
    const right = root.getBoundingClientRect().right;
    maximized = false;
    root.classList.add('ti-resizing');
    grip.setPointerCapture?.(e.pointerId);
    const move = (ev) => { width = clamp(right - ev.clientX); apply(); };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.removeEventListener('pointercancel', up);
      root.classList.remove('ti-resizing');
      save();
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
    grip.addEventListener('pointercancel', up);
  });
  grip.addEventListener('dblclick', () => { width = defaultWidth; maximized = false; save(); apply(); });
  btn.addEventListener('click', () => { maximized = !maximized; apply(); });

  const onWinResize = () => {
    if (!document.body.contains(root)) { window.removeEventListener('resize', onWinResize); return; }
    apply();
  };
  window.addEventListener('resize', onWinResize);
  apply();

  /**
   * УИ-2e — сменить ключ ширины «на лету». Нужно синглтон-панели записи
   * (record-panel): карточка проверки «Нормоконтроля» открывается в той же панели,
   * что и норматив/ИЛ, но хочет свою память ширины (`ccpm_inspector_width_normcheck`),
   * не затирая ключи 'norms'/'sheets'/'expertise'/'counterparties'. Перечитывает
   * сохранённую ширину нового ключа и сбрасывает «развёрнуто» к переданному значению.
   */
  const setKey = (newKey, opts = {}) => {
    if (!newKey || newKey === key) return;
    key = newKey;
    storeKey = `ccpm_inspector_width_${key}`;
    grip.dataset.tiGrip = key;
    btn.dataset.tiExpand = key;
    if (opts.defaultWidth != null) defaultWidth = opts.defaultWidth;
    maximized = opts.maximized ?? false;
    width = defaultWidth;
    try { const s = Number(localStorage.getItem(storeKey)); if (s > 0) width = s; } catch { /* storage недоступен */ }
    apply();
  };

  return { notify, setKey, reset: () => { width = defaultWidth; maximized = false; save(); apply(); } };
}
